import React from 'react';
import { Platform, Text } from 'react-native';

import { useAlert } from '@hooks/useAlert.web';
import { useHover } from '@hooks/useHover';
import theme from '@themes/theme';
import {
  copyLinkUrl,
  openLinkUrl,
  splitTextWithLinks,
} from '@utils/linkifyText';

interface HyperLinkProps {
  children?: JSX.Element;
}

const LinkText: React.FC<{ url: string; displayText: string }> = ({
  url,
  displayText,
}) => {
  // Inline-link rule (TA-9005): no underline at rest, underline appears on
  // hover. On native (no hover) the underline stays visible as the link
  // affordance.
  const { isHovered, hoverProps } = useHover();
  const alert = useAlert();
  const showUnderline = Platform.OS !== 'web' || isHovered;

  return (
    <Text
      style={{
        color: theme.colors.blue,
        textDecorationLine: showUnderline ? 'underline' : 'none',
      }}
      {...hoverProps}
      onPress={() => openLinkUrl(url)}
      onLongPress={() => copyLinkUrl(url, alert.showAlert)}>
      {displayText}
    </Text>
  );
};

/**
 * Recursively walks `node`, splitting any plain-string children that
 * contain a URL into sibling plain-text and `<LinkText>` segments. Clones
 * each element it touches with its own original props intact (variant,
 * color, style, etc.) -- it only ever swaps `children`, so it never loses a
 * caller's styling and never introduces a wrapping host element (no `View`,
 * no extra `Text`). That's the deliberate difference from
 * `react-native-hyperlink`, which always rendered its own `View` wrapper
 * around whatever it was given -- fine on its own, but breaks native text
 * layout when that View ends up interleaved with other inline Text content
 * (TA-10489). Removing the dependency also drops a standing patch-package
 * patch needed just to keep it from crashing on React 19 (TA-10066) and a
 * Babel/webpack transpile-path workaround needed to ship it into the web
 * bundle at all (TA-10032) -- see TA-10697.
 */
const parseNode = (
  node: React.ReactNode,
  keyPrefix: string
): React.ReactNode => {
  if (!React.isValidElement<{ children?: React.ReactNode }>(node)) return node;

  const children = node.props.children;
  if (children == null) return node;

  let didLinkify = false;

  const processedChildren = React.Children.map(children, (child, index) => {
    const childKey = `${keyPrefix}-${index}`;

    if (typeof child === 'string') {
      const segments = splitTextWithLinks(
        child,
        childKey,
        (segment, key) => <React.Fragment key={key}>{segment}</React.Fragment>,
        (url, displayText, key) => (
          <LinkText key={key} url={url} displayText={displayText} />
        )
      );
      if (!segments) return child;
      didLinkify = true;
      return <React.Fragment key={childKey}>{segments}</React.Fragment>;
    }

    if (React.isValidElement(child)) {
      const processedChild = parseNode(child, childKey);
      if (processedChild !== child) didLinkify = true;
      return processedChild;
    }

    return child;
  });

  if (!didLinkify) return node;

  return React.cloneElement(node, { ...node.props }, processedChildren);
};

export const HyperLink: React.FC<HyperLinkProps> = ({ children }) => {
  if (!children) return null;
  return <>{parseNode(children, 'hyperlink')}</>;
};
